import { t } from "i18next";
import { DataObjectField } from "../DataObjectField/DataObjectField";
import { FirebaseDataStringField } from "../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectType } from "./DataObjectHelper";

export type DataObjectFieldComparator =
  | "equals"
  | "not-equals"
  | "is-one-of"
  | "is-not-one-of"
  | "includes"
  | "contains-all"
  | "less-than"
  | "less-than-or-equal-to"
  | "greater-than"
  | "greater-than-or-equal-to"
  | "rolling-back"
  | "rolling-forward"
  | "within-period"
  | "is-known"
  | "is-unknown";
export class DataObjectFieldComparatorHelper {
  static getName(key: DataObjectFieldComparator | undefined, isArray: boolean): string {
    if (key === "equals") return t("main.equals");
    if (key === "not-equals") return t("main.notEquals");
    if (key === "is-one-of") return isArray ? t("main.isSomeOf") : t("main.isOneOf");
    if (key === "is-not-one-of") return isArray ? t("main.isNotSomeOf") : t("main.isNotOneOf");
    if (key === "includes") return t("main.includes");
    if (key === "contains-all") return t("main.containsAll");
    if (key === "less-than") return t("main.lessThan");
    if (key === "less-than-or-equal-to") return t("main.lessThanOrEqualTo");
    if (key === "greater-than") return t("main.greaterThan");
    if (key === "greater-than-or-equal-to") return t("main.greaterThanOrEqualTo");
    if (key === "rolling-back") return t("main.rollingBack");
    if (key === "rolling-forward") return t("main.rollingForward");
    if (key === "within-period") return t("main.withinPeriod");
    if (key === "is-known") return t("main.isSpecified");
    if (key === "is-unknown") return t("main.isNotSpecified");
    return "error";
  }

  /**
   * None: No input should be used
   * Single: Always display a single-value input
   * Multi: Always display as a multi-value input
   */
  static getInputType(key: DataObjectFieldComparator): "none" | "single" | "multi" | "both" {
    if (key === "equals") return "single";
    if (key === "not-equals") return "single";
    if (key === "is-one-of") return "both";
    if (key === "is-not-one-of") return "both";
    if (key === "includes") return "both";
    if (key === "contains-all") return "multi";
    if (key === "less-than") return "single";
    if (key === "less-than-or-equal-to") return "single";
    if (key === "greater-than") return "single";
    if (key === "greater-than-or-equal-to") return "single";
    if (key === "rolling-back") return "single";
    if (key === "rolling-forward") return "single";
    if (key === "within-period") return "single";
    if (key === "is-known") return "none";
    if (key === "is-unknown") return "none";
    return "none";
  }

  static doesDataFollowSimpleFilter(
    dataValue: any,
    comparator: DataObjectFieldComparator,
    filterValue: any | any[] | undefined,
    field: DataObjectField<any, any> | undefined
  ): boolean {
    if (Array.isArray(filterValue) && filterValue.length === 0) return true;
    switch (comparator) {
      case "equals":
        if (field?.equalComparator && field.fullIsValueValid(filterValue) && field.fullIsValueValid(dataValue))
          return field.equalComparator(dataValue, filterValue);

        return dataValue === filterValue;
      case "not-equals":
        if (field?.equalComparator && field.fullIsValueValid(filterValue) && field.fullIsValueValid(dataValue))
          return !field.equalComparator(dataValue, filterValue);

        return dataValue !== filterValue;
      case "is-one-of":
        if (Array.isArray(dataValue) && Array.isArray(filterValue)) return dataValue.some((dataVal) => filterValue.includes(dataVal));
        if (Array.isArray(dataValue) && !Array.isArray(filterValue) && filterValue !== undefined) return dataValue.includes(filterValue);
        if (!Array.isArray(dataValue) && Array.isArray(filterValue) && dataValue !== undefined) return filterValue.includes(dataValue);
        if (!Array.isArray(dataValue) && !Array.isArray(filterValue)) return filterValue === dataValue;
        return false;
      case "is-not-one-of":
        if (!dataValue) return true;
        if (Array.isArray(dataValue) && Array.isArray(filterValue)) return !dataValue.some((dataVal) => filterValue.includes(dataVal));
        if (Array.isArray(dataValue) && !Array.isArray(filterValue) && filterValue !== undefined) return !dataValue.includes(filterValue);
        if (!Array.isArray(dataValue) && Array.isArray(filterValue)) return !filterValue.includes(dataValue);
        if (!Array.isArray(dataValue) && !Array.isArray(filterValue)) return filterValue !== dataValue;
        return false;
      case "includes":
        if (Array.isArray(dataValue) && !Array.isArray(filterValue) && filterValue !== undefined) return dataValue.includes(filterValue);
        if (field?.includesComparator) return field.includesComparator!(dataValue, filterValue);
        return false;
      case "contains-all":
        return Array.isArray(dataValue) && Array.isArray(filterValue) && !filterValue.some((filterVal) => !dataValue.includes(filterVal));
      case "less-than":
        if (!dataValue || !filterValue) return false;
        return dataValue < filterValue;
      case "less-than-or-equal-to":
        if (
          field?.equalComparator &&
          field.fullIsValueValid(filterValue) &&
          field.fullIsValueValid(dataValue) &&
          field.equalComparator(dataValue, filterValue)
        )
          return true;
        if (!dataValue || !filterValue) return false;
        return dataValue <= filterValue;
      case "greater-than":
        if (!dataValue || !filterValue) return false;
        return dataValue > filterValue;
      case "greater-than-or-equal-to":
        if (
          field?.equalComparator &&
          field.fullIsValueValid(filterValue) &&
          field.fullIsValueValid(dataValue) &&
          field.equalComparator(dataValue, filterValue)
        )
          return true;
        if (!dataValue || !filterValue) return false;
        return dataValue >= filterValue;
      case "is-known":
        if (Array.isArray(dataValue)) return dataValue.length > 0;
        return dataValue !== undefined && dataValue !== null;
      case "is-unknown":
        if (Array.isArray(dataValue)) return dataValue.length === 0;
        return dataValue === undefined || dataValue === null;
      case "within-period":
        if (typeof filterValue === "string" && dataValue instanceof Date) {
          return doesDataFollowPeriodFilter(dataValue, filterValue);
        }
        return false;
      case "rolling-back":
        if (dataValue instanceof Date && typeof filterValue === "string") {
          const date = new Date();
          date.setDate(date.getDate() - Number.parseInt(filterValue));
          return date < dataValue;
        }
        return false;
      case "rolling-forward":
        if (dataValue instanceof Date && typeof filterValue === "string") {
          const date = new Date();
          date.setDate(date.getDate() + Number.parseInt(filterValue));
          return dataValue < date;
        }
        return false;
      default:
        return true;
    }
  }

  static getValidComparators<T extends DataObjectType>(field: DataObjectField<any, T> | undefined) {
    return (
      field?.allComparators.filter((comparator) => {
        const type = DataObjectFieldComparatorHelper.getInputType(comparator);
        if (comparator === "includes")
          return (!field.allComparators.includes("is-one-of") && field.isArray) || field instanceof FirebaseDataStringField;
        if (type === "both" || type === "none") return true;
        if (!field.isArray && type === "single") return true;
        if (field.isArray && type === "multi") return true;
        return false;
      }) ?? []
    );
  }
}

type Period = {
  key: string;
  label: string;
};

export const periods: Period[] = [
  { key: "next-week", label: t("main.nextWeek") },
  { key: "next-month", label: t("main.nextMonth") },
  { key: "next-quarter", label: t("main.nextQuarter") },
  { key: "next-year", label: t("main.nextYear") },
  { key: "last-week", label: t("main.lastWeek") },
  { key: "last-month", label: t("main.lastMonth") },
  { key: "last-quarter", label: t("main.lastQuarter") },
  { key: "last-year", label: t("main.lastYear") },
];

const doesDataFollowPeriodFilter = (dataValue: any, period: string) => {
  const date = new Date();
  switch (period) {
    case "next-week":
      date.setDate(date.getDate() + 7);
      return dataValue < date;
    case "next-month":
      date.setMonth(date.getMonth() + 1);
      return dataValue < date;
    case "next-quarter":
      date.setMonth(date.getMonth() + 3);
      return dataValue < date;
    case "next-year":
      date.setFullYear(date.getFullYear() + 1);
      return dataValue < date;
    case "last-week":
      date.setDate(date.getDate() - 7);
      return date < dataValue;
    case "last-month":
      date.setMonth(date.getMonth() - 1);
      return date < dataValue;
    case "last-quarter":
      date.setMonth(date.getMonth() - 3);
      return date < dataValue;
    case "last-year":
      date.setFullYear(date.getFullYear() - 1);
      return date < dataValue;
    default:
      return false;
  }
};
